import {
  CodeBlock,
  Heading,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  VStack,
} from '@astryxdesign/core';

/**
 * "Beat seasonal-naive first."
 *
 * Every count here is computed from benchmarks/results/per-series-*.csv — the
 * same 300 M4 series as the TimesFM vs Prophet article, read per series rather
 * than as a mean. The per-series file is published at
 * /benchmarks/m4-300-per-series.csv, so each number is one groupby away for a
 * reader who wants to check it.
 */

interface FreqRow {
  freq: string;
  /** Series where the method's MASE < 1: better than in-sample seasonal-naive. */
  timesfmUnder1: number;
  naiveUnder1: number;
  /** Series where TimesFM's MASE was above seasonal-naive's on the same series. */
  timesfmLoses: number;
  /** Quartiles of TimesFM MASE ÷ seasonal-naive MASE, per series. */
  ratioQ1: number;
  ratioMedian: number;
  ratioQ3: number;
  /** Series where TimesFM's MASE was under half of seasonal-naive's. */
  bigWins: number;
  /** Series where neither method got under 1. */
  bothOver1: number;
}

const ROWS: FreqRow[] = [
  { freq: 'Hourly', timesfmUnder1: 78, naiveUnder1: 38, timesfmLoses: 20, ratioQ1: 0.38, ratioMedian: 0.54, ratioQ3: 0.93, bigWins: 45, bothOver1: 16 },
  { freq: 'Weekly', timesfmUnder1: 43, naiveUnder1: 23, timesfmLoses: 32, ratioQ1: 0.51, ratioMedian: 0.86, ratioQ3: 1.05, bigWins: 24, bothOver1: 54 },
  { freq: 'Monthly', timesfmUnder1: 64, naiveUnder1: 53, timesfmLoses: 34, ratioQ1: 0.68, ratioMedian: 0.86, ratioQ3: 1.07, bigWins: 13, bothOver1: 32 },
];

const NAIVE = `import numpy as np

def seasonal_naive(y: np.ndarray, horizon: int, m: int) -> np.ndarray:
    """Repeat the last seasonal cycle of length m. m=1 is 'tomorrow equals today'."""
    last_cycle = y[-m:]
    reps = int(np.ceil(horizon / m))
    return np.tile(last_cycle, reps)[:horizon]

def mase(y_train: np.ndarray, y_true: np.ndarray, y_hat: np.ndarray, m: int) -> float:
    """M4's definition: error scaled by the in-sample seasonal-naive error."""
    scale = np.mean(np.abs(y_train[m:] - y_train[:-m]))
    return np.mean(np.abs(y_true - y_hat)) / scale`;

const COMPARE = `# Hold out the last H points of every series you care about, then:
for name, y in catalogue.items():
    train, test = y[:-H], y[-H:]
    base = seasonal_naive(train, H, m)
    model = forecast(train, H)["forecasts"][0]["point"]   # or Prophet, or anything
    print(name, mase(train, test, base, m), mase(train, test, model, m))
# Ship the model only on the series where its column is lower. Keep the
# baseline on the rest — it is free, and on those series it is also better.`;

/** Ratio quartiles as a small range bar per frequency, against the 1.0 line. */
function RatioChart() {
  const width = 720;
  const height = 190;
  const pad = { top: 18, right: 24, bottom: 30, left: 76 };
  const plotW = width - pad.left - pad.right;
  const rowH = (height - pad.top - pad.bottom) / ROWS.length;
  const maxRatio = 1.3;
  const x = (r: number) => pad.left + (Math.min(r, maxRatio) / maxRatio) * plotW;

  return (
    <VStack gap={1.5}>
      <div className="chart-wrap">
        <svg
          className="chart"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label="Interquartile range of TimesFM MASE divided by seasonal-naive MASE per series: Hourly 0.38 to 0.93 with median 0.54, Weekly 0.51 to 1.05 with median 0.86, Monthly 0.68 to 1.07 with median 0.86. A vertical line marks 1.0, where the two methods tie."
        >
          {[0.25, 0.5, 0.75, 1.0, 1.25].map((t) => (
            <g key={t}>
              <line x1={x(t)} x2={x(t)} y1={pad.top} y2={height - pad.bottom} stroke={t === 1 ? 'var(--color-border-emphasized)' : 'var(--color-border)'} strokeWidth={t === 1 ? 1.5 : 1} strokeDasharray={t === 1 ? '5 4' : undefined} />
              <text x={x(t)} y={height - 10} textAnchor="middle" fontSize={11} fill="var(--color-text-secondary)">
                {t === 1 ? 'tie' : `${t}×`}
              </text>
            </g>
          ))}
          {ROWS.map((r, i) => {
            const cy = pad.top + rowH * i + rowH / 2;
            return (
              <g key={r.freq}>
                <text x={pad.left - 12} y={cy + 4} textAnchor="end" fontSize={12} fill="var(--color-text-primary)">
                  {r.freq}
                </text>
                <rect x={x(r.ratioQ1)} y={cy - 9} width={x(r.ratioQ3) - x(r.ratioQ1)} height={18} rx={6} fill="var(--vt-accent)" opacity={0.28} />
                <line x1={x(r.ratioMedian)} x2={x(r.ratioMedian)} y1={cy - 11} y2={cy + 11} stroke="var(--vt-accent)" strokeWidth={3} strokeLinecap="round" />
                <text x={x(r.ratioQ3) + 8} y={cy + 4} fontSize={11} fill="var(--color-text-secondary)">
                  {`median ${r.ratioMedian.toFixed(2)}×`}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
      <Text size="sm" color="secondary">
        Bars span the middle half of series; the thick mark is the median. Anything left of the
        dashed line is a series where TimesFM beat seasonal-naive; the right end of the Weekly and
        Monthly bars crosses it.
      </Text>
    </VStack>
  );
}

export function SeasonalNaiveBaselineArticle() {
  return (
    <>
      <Text>
        Seasonal-naive means: predict that the next cycle looks exactly like the last one. It fits
        nothing, costs nothing and is the baseline every published M4 result is scaled against. It is
        also, on the 300 series in our benchmark, the method that beat a 200-million-parameter
        foundation model on 20% of hourly series, 32% of weekly series and 34% of monthly series. If
        you are choosing a forecasting method, this is the comparison to run before any other, and
        this post is the per-series evidence for why.
      </Text>

      <div className="callout">
        <Text weight="medium">The short version</Text>
        <Text color="secondary">
          Averages flatter the model. Per series, TimesFM zero-shot cleared the in-sample naive bar
          (MASE below 1) on 78, 43 and 64 of 100 series by frequency, against 38, 23 and 53 for
          seasonal-naive itself. The median series is a clear win; the upper quartile on weekly and
          monthly data is a loss. Run the baseline on your own series and ship the model only where
          it wins.
        </Text>
      </div>

      <VStack gap={2}>
        <Heading level={2} id="what-seasonal-naive-is">What seasonal-naive is</Heading>
        <Text color="secondary">
          Take the last full season of length <em>m</em> — 24 for hourly data with a daily cycle, 12
          for monthly data with a yearly one — and repeat it for as many steps as you need. For weekly
          and daily data M4 sets <em>m</em> to 1, which turns the method into &ldquo;next week equals
          this week&rdquo;. That sounds like a straw man until you try to beat it on a series with a
          strong cycle and weak trend, which describes most operational data.
        </Text>
        <Text color="secondary">
          It is the right baseline for two reasons. It encodes the one thing almost every business
          series has — periodicity — and nothing else, so beating it proves the model has learned
          something beyond the calendar. And it is deterministic, so a comparison against it is
          reproducible by anyone with the series and ten lines of NumPy.
        </Text>
      </VStack>

      <VStack gap={2}>
        <Heading level={2} id="why-mase">Why MASE is the right ruler</Heading>
        <Text color="secondary">
          MASE divides a method&apos;s mean absolute error by the mean absolute error of seasonal-naive
          on the <em>training</em> part of the same series. A value of 1 means the method did as well
          out of sample as naive did in sample; below 1 is better, above 1 is worse. It is scale-free,
          so an error on a series measured in thousands and one measured in tenths sit on the same
          axis, and it is defined against the baseline this article is about, so the threshold is
          meaningful rather than arbitrary.
        </Text>
        <Text color="secondary">
          One subtlety that catches people: seasonal-naive&apos;s own out-of-sample MASE is not 1. It
          is scaled by its in-sample error, and the future is usually harder than the past, so it
          lands above 1 on most series — 62 of 100 hourly series, for instance. That is why the table
          below reports both methods against the same bar rather than treating naive as the bar.
        </Text>
      </VStack>

      <VStack gap={2}>
        <Heading level={2} id="per-series">Per series, not on average</Heading>
        <Text color="secondary">
          The summary table in the{' '}
          <Link href="/blog/timesfm-vs-prophet-benchmark#results">benchmark article</Link> says
          TimesFM won every frequency on mean MASE. True, and not very informative: a mean over 100
          series can be carried by a dozen large wins. These are the same 300 series, counted.
        </Text>
        <Table density="balanced" dividers="rows">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Frequency</TableHeaderCell>
              <TableHeaderCell>TimesFM MASE &lt; 1</TableHeaderCell>
              <TableHeaderCell>Naive MASE &lt; 1</TableHeaderCell>
              <TableHeaderCell>TimesFM at under half naive&apos;s error</TableHeaderCell>
              <TableHeaderCell>Neither under 1</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ROWS.map((r) => (
              <TableRow key={r.freq}>
                <TableCell>
                  <Text size="sm">{r.freq}</Text>
                </TableCell>
                <TableCell>
                  <span className="mono">{r.timesfmUnder1} / 100</span>
                </TableCell>
                <TableCell>
                  <span className="mono">{r.naiveUnder1} / 100</span>
                </TableCell>
                <TableCell>
                  <span className="mono">{r.bigWins} / 100</span>
                </TableCell>
                <TableCell>
                  <span className="mono">{r.bothOver1} / 100</span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <RatioChart />
        <Text color="secondary">
          Hourly is the clean case: the model halves the naive error on 45 series and loses on 20. On
          weekly and monthly data the middle of the distribution is a modest win — a median ratio of
          0.86 on both — and the upper quartile is a loss. Read that as: on a typical monthly series
          the model shaves 14% off the naive error, and on one series in three it adds to it.
        </Text>
      </VStack>

      <VStack gap={2}>
        <Heading level={2} id="where-it-loses">Where the model loses</Heading>
        <Text color="secondary">
          The last column of the table is the one to sit with. On 54 of 100 weekly series and 32 of
          100 monthly series, <em>neither</em> method got under 1 — the future was simply harder than
          the past for both, and the question of which lost by less is secondary to the fact that
          both lost. Those are series with level shifts, ends of trends, or a last season that was
          not representative, and no method that only sees the history can do much with them.
        </Text>
        <Text color="secondary">
          When TimesFM does lose to naive, it loses narrowly: the median losing margin is 1.46× on
          hourly, 1.21× on weekly and 1.14× on monthly. The wins are larger than the losses, which is
          how the means come out in the model&apos;s favour while a third of series do not. The
          worst single series for TimesFM scored a MASE of 12.1 on weekly data; naive&apos;s worst on
          the same frequency was 14.2. Neither is a number you would want in production, and both are
          in the per-series CSV.
        </Text>
      </VStack>

      <VStack gap={2}>
        <Heading level={2} id="run-the-baseline">Run the baseline in ten lines</Heading>
        <Text color="secondary">
          There is no library to install. The baseline and the metric together are shorter than most
          import blocks.
        </Text>
        <div className="code-frame">
          <CodeBlock code={NAIVE} language="python" hasCopyButton container="card" />
        </div>
        <Text color="secondary">
          Then hold out the last horizon of every series, forecast it with the baseline and with
          whatever you are evaluating, and compare the two columns series by series. Not the mean of
          the columns — the columns.
        </Text>
        <div className="code-frame">
          <CodeBlock code={COMPARE} language="python" hasCopyButton container="card" />
        </div>
        <Text size="sm" color="secondary">
          The published benchmark script does this for M4 with{' '}
          <Link href="/benchmarks/run.py" isExternalLink>
            one command
          </Link>
          ; the per-series output it wrote is at{' '}
          <Link href="/benchmarks/m4-300-per-series.csv" isExternalLink>
            m4-300-per-series.csv
          </Link>
          , and every count in this article is a groupby over it.
        </Text>
      </VStack>

      <VStack gap={2}>
        <Heading level={2} id="when-naive-is-enough">When naive is the right answer</Heading>
        <VStack gap={1.5}>
          <Text color="secondary">
            <strong>The series is strongly periodic and flat.</strong> Hourly traffic to a stable
            service, weekly sales of a mature product: last cycle is a very good guess, and a model
            that mostly reproduces it is paying for nothing. Keep naive, and spend the forecasting
            budget on the series where it fails.
          </Text>
          <Text color="secondary">
            <strong>You need to explain the number.</strong> &ldquo;Same as last week&rdquo; is an
            explanation everyone in the room accepts. A foundation model&apos;s output is not, and on a
            series where the two are within a few percent, the explainable one wins the meeting.
          </Text>
          <Text color="secondary">
            <strong>You have not measured yet.</strong> The most common failure is not choosing the
            wrong method; it is choosing any method without the baseline column next to it. Until
            that column exists, naive is the honest default — it is what you would have to beat to
            justify anything else.
          </Text>
        </VStack>
      </VStack>
    </>
  );
}
